import React, { useEffect } from "react";
import moment from "moment";
import "moment-timezone";
// Wallet Balance

// Default
import { useState } from "react";

// Mui
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import CardContent from "@mui/material/CardContent";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import TablePagination from "@mui/material/TablePagination";
import RefreshIcon from "@mui/icons-material/Refresh";

// Custom
import CustomInput from "../../forms/input/Input";
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import CustomToast from "../toast/CustomToast";
import BackdropSpinner from "../spinner/BackdropSpinner";
import BillingDetailsModal from "../modals/BillingDetailsModal";
import CustomSearch from "../../components/search/Search";

// React icons
import { FaArrowUp } from "react-icons/fa";
import { FaArrowDown } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa";

// Svg
import Calendar2 from "../../assets/svg/calendar2";

// Redux
import {
  setBalance,
  setTransactionsHistory,
  setWorkOrderHistory,
  setBillingDeterminants,
  setWorkOrderId,
  setBillingData,
  setSelectedBillingData,
  setSelectedBillingDeterminant,
  setSelectedMeter,
  setWalletBalance,
} from "../../redux/features/BillingDeterminantSlice";

// Utils
import {
  fetchGETRequest,
  fetchPUTRequest,
  fetchPOSTRequest,
} from "../../utils/services";
import {
  getTimeStamp,
  formatTime,
  formatDate,
  isUUID,
} from "../../utils/helper";
import useEmailJS from "../../hooks/useEmailJS";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  BillingDeterminationItem,
  ToastState,
  TransactionItem,
} from "../../types";
import { UnifiedIcon } from "../Icon/UnifiedIcon";

interface WorkOrderItem {
  DeviceID: string;
  LastUpdatedAt: string;
  Command: string;
  CreatedAt: string;
  WorkOrderID: string;
  Status?: string;
  MeterName?: string;
  MeterID?: string;
}

interface APIResponse {
  data?: {
    data?: WorkOrderItem[];
  };
  WorkOrderID?: string;
  Status?: string;
  IP_KWH?: Array<{ value: string }>;
  EventCode?: Array<{ value: string }>;
  [key: string]: any;
}

interface EmailData {
  balance: number;
  message: string;
  email: string;
}

interface ContractItem {
  id: string;
  contractNumber: string;
  contractType: string;
  [key: string]: any;
}

const ConsumerDrawerBillAndPayment = ({
  meterId,
  onRefresh,
}: {
  meterId: string;
  onRefresh?: () => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [toast, setToast] = useState<ToastState>({
    message: "",
    severity: "info",
  });
  const [loader, setLoader] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [contracts, setContracts] = useState<ContractItem[]>([]);
  const [selectedContract, setSelectedContract] = useState<ContractItem | null>(
    null,
  );
  const [topupResponse, setTopupResponse] = useState<{
    transactionNumber: string;
    balanceBefore: number;
    balanceAfter: number;
    amount: number;
  } | null>(null);
  const dispatch = useAppDispatch();

  const {
    balance,
    transactionsHistory,
    workOrderHistory,
    billingDeterminants,
    billingData,
    selectedBillingDeterminant,
    selectedBillingData,
    selectedMeter,
    walletBalance,
    workOrderId,
  } = useAppSelector((state) => state.billing);
  const { customerId } = useAppSelector((state) => state.customer);

  const [transactionPage, setTransactionPage] = useState(0);
  const [transactionRowsPerPage, setTransactionRowsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [transactionTypeFilter, setTransactionTypeFilter] =
    useState("All Types");

  const allMeters = [selectedMeter];

  const { sendEmail } = useEmailJS();

  const AppSendMail = async (balance: number, message: string) => {
    const emailData: EmailData = {
      balance: balance,
      message: message,
      email: "kaushik@voltreum.com",
    };

    console.log(emailData, "---");

    await sendEmail(emailData, meterId || selectedMeter)
      .then((res: any) => {
        console.log("Email sent:", res?.text);
        setToast({
          message: "Email sent successfully!",
          severity: "success",
        });
      })
      .catch((err: unknown) => {
        console.error("Failed to send email:", err);
        setToast({
          message: err instanceof Error ? err.message : "Email failed",
          severity: "error",
        });
      });
  };

  const fetchWorkOrders = async () => {
    const targetMeter = meterId || selectedMeter;
    if (!targetMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(targetMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    try {
      const response: any = await fetchGETRequest(
        `devices/${targetMeter}/workorders`,
        {},
      );

      if (targetMeter === (meterId || selectedMeter)) {
        const getNormalizedTime = (item: WorkOrderItem) => {
          if (!item?.LastUpdatedAt) return 0;
          const isHES = item?.Status?.toLowerCase()
            .trim()
            .includes(" received from hes ");
          if (isHES) {
            return moment
              .utc(item.LastUpdatedAt, "MM/DD/YYYY HH:mm:ss")
              .tz("Asia/Kolkata")
              .valueOf();
          }
          return moment(item.LastUpdatedAt, [
            "MM/DD/YYYY HH:mm:ss",
            moment.ISO_8601,
          ]).valueOf();
        };

        const sortedData = response?.data?.sort(
          (a: WorkOrderItem, b: WorkOrderItem) => {
            const timeA = getNormalizedTime(a);
            const timeB = getNormalizedTime(b);
            const diff = timeB - timeA;

            if (diff === 0) {
              // If timestamps match, prioritize non-IN PROGRESS records (outcomes)
              const aIsProgress =
                a.Status?.toUpperCase().includes("IN PROGRESS");
              const bIsProgress =
                b.Status?.toUpperCase().includes("IN PROGRESS");
              if (aIsProgress && !bIsProgress) return 1;
              if (!aIsProgress && bIsProgress) return -1;
            }
            return diff;
          },
        );
        dispatch(setWorkOrderHistory(sortedData || []));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchContracts = async () => {
    if (!customerId) return;
    try {
      const response: any = await fetchGETRequest(
        `v2/Customers/${customerId}/contracts`,
      );
      if (response && response.success && response.data) {
        setContracts(response.data);
        if (response.data.length > 0) {
          setSelectedContract(response.data[0]);
        }
      }
    } catch (error) {
      console.error("Failed to fetch contracts:", error);
    }
  };

  useEffect(() => {
    const targetMeter = meterId || selectedMeter;
    if (targetMeter) {
      setTimeout(() => {
        handleFetchWalletHistory();
        fetchWorkOrders();
      }, 200);
    }
  }, [meterId, selectedMeter, customerId]);

  useEffect(() => {
    if (customerId) {
      handleFetchWalletBalance();
      fetchContracts();
    }
  }, [customerId]);

  useEffect(() => {
    dispatch(setSelectedMeter(meterId));
  }, [meterId]);

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleTimeRangeChange = (timeRange: number | number[]) => {
    if (Array.isArray(timeRange)) {
      setTimeRange(timeRange);
    } else {
      setTimeRange([timeRange, timeRange]);
    }
  };

  const getTransactionTypeColor = (type?: string) => {
    switch (type?.toUpperCase()) {
      case "BILLING":
        return "#f44336"; // Red
      case "ADJUSTMENT":
        return "#ff9800"; // Orange
      case "PAYMENT":
        return "#4caf50"; // Green
      default:
        return "#757575"; // Grey
    }
  };

  const WorkOrderHistoryCard = (item: WorkOrderItem, index: number) => {
    return (
      <Box
        className="wallet_balance_work_order_history_row"
        sx={{ background: "#e3f1fa" }}
        key={index}
      >
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography
            variant="body2"
            fontWeight={"600"}
            color={item?.Command === "DISCONNECT" ? "#FF3B30" : "#9CE976"}
          >
            {item?.Command === "DISCONNECT"
              ? "Disconnect Work Order"
              : "Reconnect Work Order"}
          </Typography>
          <Typography variant="body2" fontWeight={400}>
            {formatDate(item?.CreatedAt, "DD-MM-YYYY HH:mm:ss")}
          </Typography>
        </Stack>

        <Typography variant="body2" mt={"10px"} fontWeight={500}>
          Work Order ID:{" "}
          <Typography variant="caption"> {item?.WorkOrderID}</Typography>
        </Typography>
      </Box>
    );
  };

  const GenerateBillingDeterminantCard = (
    item: BillingDeterminationItem,
    index: number,
  ) => {
    return (
      <Box
        className="wallet_balance_billing_determinant_row"
        sx={{
          backgroundColor: "#e3f1fa",
          "&:hover": {
            backgroundColor: "#e0f7fa",
          },
          cursor: "pointer",
        }}
        onClick={() => {
          const savedBillingData = billingData?.filter(
            (bill: any) => bill?.id === item?.id,
          );
          dispatch(setSelectedBillingData(savedBillingData));
          dispatch(setSelectedBillingDeterminant(item));
          setOpenModal(true);
        }}
        key={index}
      >
        <Typography variant="body2" fontWeight={"500"}>
          Billing Determinant:{" "}
          {formatDate(new Date(item?.fromDate), "DD/MM/YYYY HH:mm:ss")}{" "}
          <Typography component="span">
            <UnifiedIcon icon={FaArrowRight} color="#181B25" fontSize="10px" />
          </Typography>{" "}
          {formatDate(new Date(item?.toDate), "DD/MM/YYYY HH:mm:ss")}{" "}
        </Typography>
        <Typography variant="body2" fontWeight={400}>
          {formatDate(item?.timestamp, "DD-MM-YYYY HH:mm:ss")}
        </Typography>
      </Box>
    );
  };

  const createWorkOrder = async (type: string) => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }
    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    try {
      let body = {
        deviceIds: [selectedMeter],
        command: "",
        projectName: "",
      };

      if (type === "disconnect") {
        body.command = "ExecuteDisconnect";
      } else {
        body.command = "ExecuteConnect";
      }

      const response: APIResponse = await fetchPUTRequest(
        `devices/command`,
        body,
      );
      if (response) {
        const newWorkOrder: WorkOrderItem = {
          DeviceID: selectedMeter,
          Command: type === "disconnect" ? "DISCONNECT" : "RECONNECT",
          CreatedAt: moment().format("DD/MM/YYYY HH:mm:ss"),
          LastUpdatedAt: moment().toISOString(),
          WorkOrderID: response?.WorkOrderID || "PENDING",
          Status: "IN PROGRESS",
        };
        dispatch(setWorkOrderHistory([newWorkOrder, ...workOrderHistory]));
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        severity: "error",
      });
    }
  };

  const handleFetchLastRecharge = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    try {
      setLoader(true);
      const body = {
        command: "GetLastTokenRechargeAmount",
        deviceIds: allMeters,
      };
      const response: any = await fetchPUTRequest(`devices/command`, body);

      if (response && response.WorkOrderID) {
        dispatch(setWorkOrderId(response.WorkOrderID));
        // const workOrderHistoryDetails = await fetchGETRequest(
        //   `workorders/${response?.WorkOrderID}`
        // );
      }
    } catch (error) {
      setToast({
        message:
          "Wallet balance unavailable. Possible network delay or data source issue. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const submitRechargeAmount = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    setToast({
      message: "Recharge Updation in process, Try refresh after some time.",
      severity: "info",
    });
    const body = {
      command: "SetLastTokenRechargeAmount",
      value: rechargeAmount,
      deviceIds: allMeters,
    };
    const response: any = await fetchPUTRequest(
      // `devices/${selectedMeter}/prepaidparam`,
      `devices/prepaidparam`,
      body,
    );

    if (response && response.WorkOrderID) {
      dispatch(setWorkOrderId(response.WorkOrderID));

      const workOrderHistoryDetails = await fetchGETRequest(
        `workorders/${response.WorkOrderID}`,
      );
    }
  };

  const handleFetchWalletBalance = async () => {
    try {
      if (!customerId) {
        setToast({
          message: "Customer ID is required.",
          severity: "error",
        });
        return;
      }

      setLoader(true);
      const response: any = await fetchGETRequest(
        `v2/Balances/customer/${customerId}`,
      );

      if (response && response.success && response.data) {
        dispatch(setBalance(response.data.totalBalance || 0));
      } else {
        setToast({
          message: response?.message || "Failed to fetch wallet balance.",
          severity: "error",
        });
      }
    } catch (error) {
      setToast({
        message:
          "Wallet balance unavailable. Possible network delay or data source issue. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const handleFetchWalletHistory = async (
    page: number = 0,
    pageSize: number = 10,
  ) => {
    try {
      const targetMeter = meterId || selectedMeter;

      if (!customerId) {
        if (!targetMeter) return;
        const body = {
          command: "GetWalletTransactions",
          deviceIds: [targetMeter],
          projectName: "",
          value: String(pageSize),
        };
        setLoader(true);
        const response = await fetchPUTRequest<{
          value: {
            Id: string;
            MeterId: string;
            Amount: number;
            CreatedAt: string;
          }[];
        }>(`devices/command`, body);

        if (targetMeter === (meterId || selectedMeter)) {
          const newData: TransactionItem[] = response?.value?.map((item) => ({
            id: item.Id,
            amount: item.Amount,
            timestamp: item.CreatedAt,
            animate: false,
          }));
          dispatch(setTransactionsHistory(newData || []));
          setTotalTransactions(newData?.length || 0);
        }
        return;
      }

      setLoader(true);
      let url = `v2/transactions?customerId=${customerId}&page=${page + 1}&pageSize=${pageSize}`;
      if (transactionTypeFilter !== "All Types") {
        url += `&transactionType=${transactionTypeFilter}`;
      }
      const response: any = await fetchGETRequest(url);

      if (response && response.success && response.data) {
        const newData: TransactionItem[] = response.data.map((item: any) => ({
          id: item.id,
          amount: item.amount,
          timestamp: item.createdAt,
          animate: false,
          transactionNumber: item.transactionNumber,
          transactionType: item.transactionType,
          contractNumber: item.contractNumber,
          balanceBefore: item.balanceBefore,
          balanceAfter: item.balanceAfter,
          description: item.description,
        }));

        dispatch(setTransactionsHistory(newData));
        setTotalTransactions(response.totalCount || newData.length);
      }
    } catch (error) {
      setToast({
        message: "Failed to fetch transaction history. Please try again later.",
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setTransactionPage(newPage);
    handleFetchWalletHistory(newPage, transactionRowsPerPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setTransactionRowsPerPage(newRowsPerPage);
    setTransactionPage(0);
    handleFetchWalletHistory(0, newRowsPerPage);
  };

  useEffect(() => {
    if (customerId) {
      handleFetchWalletHistory(transactionPage, transactionRowsPerPage);
    }
  }, [customerId]);

  const submitUpdateBalance = async () => {
    if (!customerId) {
      setToast({
        message: "Customer ID is required.",
        severity: "error",
      });
      return;
    }

    if (!selectedContract) {
      setToast({
        message: "Please select a contract.",
        severity: "error",
      });
      return;
    }

    if (!walletBalance) {
      setToast({
        message: "Please enter an amount.",
        severity: "error",
      });
      return;
    }

    setToast({
      message: "Wallet top-up in progress...",
      severity: "info",
    });
    try {
      setLoader(true);
      const amountToAdd = Number(walletBalance || 0);

      const body = {
        customerId: customerId,
        contractId: selectedContract.id,
        amount: amountToAdd,
        description: "",
      };

      const response: any = await fetchPOSTRequest(`v2/Wallet/topup`, body);

      if (response && response.success) {
        setTopupResponse(response.data);
        setToast({
          message: response.message || "Wallet topped up successfully",
          severity: "success",
        });

        // Add transaction to history locally for immediate feedback if needed,
        // though handleFetchWalletHistory will be called
        const newTransaction: TransactionItem = {
          id:
            response.data.transactionNumber || transactionsHistory?.length + 1,
          amount: amountToAdd,
          timestamp: new Date().toISOString(),
          animate: true,
          transactionNumber: response.data.transactionNumber,
          balanceBefore: response.data.balanceBefore,
          balanceAfter: response.data.balanceAfter,
          description: response.message,
        };

        dispatch(
          setTransactionsHistory([newTransaction, ...transactionsHistory]),
        );

        handleFetchWalletHistory();
        dispatch(setWalletBalance(""));
        dispatch(setBalance(response.data.balanceAfter));

        if (onRefresh) {
          onRefresh();
        }
      } else {
        setToast({
          message:
            response?.message || "Failed to top up wallet. Please try again.",
          severity: "error",
        });
      }
    } catch (error: any) {
      const errorMsg =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred during top-up";
      setToast({
        message: errorMsg,
        severity: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  const handleUpdateBalance = async () => {
    await submitUpdateBalance();
  };

  const handleBillingDeterminant = async () => {
    if (!selectedMeter) {
      setToast({
        message: "Please Enter the meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }

    if (!isUUID(selectedMeter)) {
      setToast({
        message: "Please Enter the valid meter serial No.",
        severity: "error",
      });
      setAnchorEl(null);
      return;
    }
    // const deviceId = "30d33fa0-bb60-11ef-85d5-e5b1b2e5df91"
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));
    const keys = "IP_KWH,EventCode";
    const intervalType = "WEEK";

    const response: any = await fetchGETRequest(
      `plugins/telemetry/DEVICE/${selectedMeter}/getBillingDeterminants?keys=${keys}&startTs=${startTs}&endTs=${endTs}&intervalType=${intervalType}`,
    );

    if (response?.IP_KWH && response?.IP_KWH?.length > 0) {
      const values = response?.IP_KWH?.map((item: { value: string }) =>
        parseFloat(item.value),
      );
      const max = Math.max(...values);
      const min = Math.min(...values);
      const totalCost = max - min;

      let penaltyCount = 0;

      const penalty = response?.EventCode?.map((item: { value: string }) => {
        if (parseFloat(item.value) >= 200 && parseFloat(item.value) < 300) {
          return penaltyCount++;
        }
      });

      const newBalance = balance - totalCost;

      const newBillingDeterminant: BillingDeterminationItem = {
        id: billingDeterminants?.length + 1,
        fromDate: formatDate(startTs, "YYYY-MM-DDTHH:mm"),
        toDate: formatDate(endTs, "YYYY-MM-DDTHH:mm"),
        timestamp: new Date().toISOString(),
      };

      const newTransaction: TransactionItem = {
        id: transactionsHistory?.length + 1,
        amount: -totalCost,
        timestamp: new Date().toISOString(),
        animate: true,
      };

      dispatch(
        setTransactionsHistory([...transactionsHistory, newTransaction]),
      );

      dispatch(
        setBillingDeterminants([...billingDeterminants, newBillingDeterminant]),
      );

      const newBillingData = {
        id: newBillingDeterminant.id,
        seqNo: billingDeterminants.length + 1,
        registerType: selectedMeter,
        qu: max.toFixed(2),
        activeEnergy: "1.0.1.8.0.255",
        ut: min.toFixed(2),
        qt: totalCost.toFixed(2),
        penaltyCount,
      };

      dispatch(setBillingData([...billingData, newBillingData]));
      // localStorage.setItem(`billingData_${newBillingDeterminant.id}`, JSON.stringify(newBillingData));
      // dispatch(setBalance(newBalance));
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);

      // if (newBalance < 20) {
      //   // await createWorkOrder('disconnect');
      //   setToast({ message: "Wallet balance is below 20. Disconnect work order created.", severity: "error" });
      // }
    } else {
      setStartDate(new Date());
      setEndDate(new Date());
      setTimeRange([0, 1439]);
      setToast({
        message: "No billing data found for the selected time range.",
        severity: "error",
      });
    }

    setAnchorEl(null);
  };

  const bulkConnectAndDisConnect = async (type: string) => {
    try {
      if (type === "disconnect") {
        setDisconnectLoading(true);
      } else {
        setConnectLoading(true);
      }

      setToast({
        message: "Bulk action in progress.",
        severity: "info",
      });

      const response: any = await fetchGETRequest(
        `tenant/devices/?pageSize=20&page=0`,
      );

      if (response?.data?.length > 0) {
        let body = {
          deviceIds: response?.data?.map((item: any) => item?.id?.id),
          command: "",
          projectName: "",
        };
        if (type === "disconnect") {
          body.command = "ExecuteDisconnect";
        } else {
          body.command = "ExecuteConnect";
        }
        await fetchPUTRequest(`devices/command`, body);
      }
    } catch (error) {
      setToast({
        message: error instanceof Error ? error.message : "An error occurred",
        severity: "error",
      });
    } finally {
      setDisconnectLoading(false);
      setConnectLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <BackdropSpinner open={loader} />
      <CustomToast
        open={toast?.message}
        severity={toast?.severity}
        setToast={setToast}
      />

      <Grid container spacing={2.25}>
        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            {/* <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}>
                  <Typography variant="body1" fontWeight={500}>
                    Billing Details
                  </Typography>

                  <Stack
                    direction={"row"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                    gap={1}>
                    <Typography
                      variant="caption"
                      fontWeight={"600"}
                      textAlign={"left"}>
                      Meter Serial No:
                    </Typography>
                    <Box sx={{ width: "400px" }}>
                      <CustomSearch
                        sx={{ width: "100%" }}
                        value={selectedMeter}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          dispatch(setSelectedMeter(e.target.value));
                        }}
                        onClick={fetchWorkOrders}
                      />
                    </Box>
                  </Stack>
                </Stack>
              }
            />
            <Divider /> */}

            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                flexGrow: 1,
              }}
            >
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography
                  variant="body1"
                  fontWeight={"600"}
                  sx={{ display: "flex", alignItems: "center", gap: "5px" }}
                >
                  Wallet Balance:
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: "600" }}
                    color={balance >= 0 ? "#FF3B30" : " #0cb254"}
                  >
                    {" "}
                    {Number(balance) >= 0
                      ? (Number(balance) * -1).toFixed(2)
                      : (Number(balance) * -1).toFixed(2)}{" "}
                    &#8377;
                  </Typography>{" "}
                </Typography>
                <Stack justifyContent={"center"} direction={"row"} gap={1}>
                  <ContainedButton
                    label={"Fetch Wallet Balance"}
                    sx={{
                      width: "fit-content",
                      color: "#ffffff",
                    }}
                    onClick={handleFetchWalletBalance}
                  />
                  {/* <IconButton
                    disabled={!workOrderId}
                    onClick={async () => {
                      if (workOrderId) {
                        const workOrderHistoryDetails: any =
                          await fetchGETRequest(`workorders/${workOrderId}`);
                        if (workOrderHistoryDetails?.Status !== "IN PROGRESS") {
                          const balanceValue = parseFloat(
                            workOrderHistoryDetails?.Status || "0",
                          );
                          if (!isNaN(balanceValue)) {
                            dispatch(setBalance(balanceValue));
                          }
                        } else {
                          setToast({
                            message:
                              "Prepaid engine has not responded yet. The request is still in progress. Please try after some time.",
                            severity: "info",
                          });
                        }
                      } else {
                        setToast({
                          message: "Workorder id is required.",
                          severity: "error",
                        });
                      }
                    }}
                    //loading={loading}
                  >
                    <RefreshIcon />
                  </IconButton> */}
                </Stack>
              </Stack>

              <Typography variant="body1">Select Contract: </Typography>
              <select
                value={selectedContract?.id || ""}
                onChange={(e) => {
                  const contract = contracts.find(
                    (c) => c.id === e.target.value,
                  );
                  if (contract) setSelectedContract(contract);
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "10px",
                  border: "1px solid #e0e0e0",
                  backgroundColor: "#fff",
                  marginBottom: "0px",
                  fontSize: "14px",
                }}
              >
                <option value="" disabled>
                  Select a contract
                </option>
                {contracts.map((contract) => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contractNumber} ({contract.contractType})
                  </option>
                ))}
              </select>

              {/* <Typography variant="body1">: </Typography> */}
              <CustomInput
                placeholder={"Enter amount"}
                type="number"
                name={"walletBalance"}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  dispatch(setWalletBalance(e.target.value));
                }}
                value={walletBalance}
              />

              <ContainedButton
                label={"Update Balance"}
                sx={{
                  width: "100%",
                  color: "#ffffff",
                  marginTop: "auto",
                }}
                //onClick={handleUpdateBalance}
                onClick={submitUpdateBalance}
                disabled={!walletBalance || !selectedContract}
              />

              {topupResponse && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    borderRadius: "10px",
                    backgroundColor: "#f0f9ff",
                    border: "1px solid #bae6fd",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="#0369a1"
                    gutterBottom
                  >
                    Last Top-up Details
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      variant="caption"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <span>Transaction #:</span>
                      <strong>{topupResponse.transactionNumber}</strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <span>Amount:</span>
                      <strong>&#8377; {topupResponse.amount.toFixed(2)}</strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <span>Balance Before:</span>
                      <strong>
                        &#8377;{" "}
                        {Number(topupResponse.balanceBefore * -1).toFixed(2)}
                      </strong>
                    </Typography>
                    <Typography
                      variant="caption"
                      display="flex"
                      justifyContent="space-between"
                    >
                      <span>Balance After:</span>
                      <strong>
                        &#8377;{" "}
                        {Number(topupResponse.balanceAfter * -1).toFixed(2)}
                      </strong>
                    </Typography>
                  </Stack>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* <Card
            sx={{
              marginTop: "20px",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}>
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}>
                  <Typography variant="body1" fontWeight={"600"}>
                    Last Recharge
                  </Typography>
                  <ContainedButton
                    label={"Fetch Last Recharge"}
                    
                    sx={{
                      width: "fit-content",
                      color: "#ffffff",
                    }}
                    disabled={!selectedMeter}
                    onClick={handleFetchLastRecharge}
                  />
                </Stack>
              }
            />
            <Divider />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                flexGrow: 1,
              }}>
              <Typography variant="body1" fontWeight={"500"}>
                Last Recharge :{" "}
                <Typography component="span" color="#9CE976">
                  &#8377; 0.00
                </Typography>{" "}
              </Typography>

              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}>
                <Typography variant="body1" fontWeight={"500"}>
                  Set Recharge Date:
                </Typography>
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    setAnchorEl(event.currentTarget)
                  }>
                  <Calendar2 />
                </IconButton>
              </Stack>

              <CustomInput
                placeholder={"Set Recharge amount"}
                type="number"
                value={rechargeAmount}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRechargeAmount(e.target.value)
                }
              />

              <ContainedButton
                label={"Set Recharge"}
                
                sx={{
                  width: "100%",
                  color: "#ffffff",
                  marginTop: "auto",
                }}
                // onClick={submitRechargeAmount}
                disabled={!rechargeAmount}
              />
            </CardContent>
          </Card> */}

          {/* <Card
            sx={{
              marginTop: "20px",
              overflow: "auto",
              height: "270px",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body1" fontWeight={"600"}>
                    Generate Billing Determinant
                  </Typography>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                      setAnchorEl(event.currentTarget)
                    }
                  >
                    <Calendar2 />
                  </IconButton>
                </Stack>
              }
            />
            <Divider />

            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: "15px" }}
            >
              {billingDeterminants?.map(
                (item: BillingDeterminationItem, index: number) => {
                  return GenerateBillingDeterminantCard(item, index);
                }
              )}

              {billingDeterminants?.length === 0 && (
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography variant="body1">No Data found</Typography>
                </Stack>
              )}

              <DateAndTimePopover
                anchorEl={anchorEl}
                setAnchorEl={(anchorEl: HTMLElement | null) =>
                  setAnchorEl(anchorEl)
                }
                startDate={startDate}
                endDate={endDate}
                timeRange={timeRange}
                setTimeRange={handleTimeRangeChange}
                handleDateChange={handleDateChange}
                handleDateSubmit={handleBillingDeterminant}
                btnText={"Generate Billing Determinant"}
              />
              <BillingDetailsModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                selectedBillingData={selectedBillingData}
                selectedBillingDeterminant={selectedBillingDeterminant}
                setSelectedBillingDeterminant={() => {
                  dispatch(setSelectedBillingDeterminant);
                }}
              />
            </CardContent>
          </Card> */}
        </Grid>

        <Grid size={{ xs: 12, md: 12, lg: 12 }}>
          {/* <Card
            sx={{
              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="body1" fontWeight={"600"}>
                    Bulk Actions
                  </Typography>

                  <Stack
                    direction={"row"}
                    gap={"10px"}
                    alignItems={"center"}
                    width={"80%"}
                    justifyContent={"flex-end"}
                  >
                    <ContainedButton
                      label={"Bulk Connect"}
                      
                      sx={{
                        width: "fit-content",
                        color: "#ffffff",
                        marginTop: "auto",
                      }}
                      onClick={() => bulkConnectAndDisConnect("connect")}
                      loading={connectLoading}
                    />

                    <ContainedButton
                      label={"Bulk Disconnect"}
                      bgColor={
                        "linear-gradient(180deg, #FF7F7F 0%, #FF4D4D 100%)"
                      }
                      sx={{
                        width: "fit-content",
                        color: "#ffffff",
                        marginTop: "auto",
                      }}
                      onClick={() => bulkConnectAndDisConnect("disconnect")}
                      loading={disconnectLoading}
                    />
                  </Stack>
                </Stack>
              }
            />
          </Card> */}

          <Card
            className="wallet_balance_transaction_history"
            sx={{
              marginTop: "20px",

              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            <CardHeader
              title={
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body1" fontWeight={"600"}>
                    Transaction History
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <select
                      value={transactionTypeFilter}
                      onChange={(e) => setTransactionTypeFilter(e.target.value)}
                      style={{
                        padding: "4px 8px",
                        borderRadius: "6px",
                        backgroundColor: "#ffffffff",
                        color: "#000000ff",
                        border: "1px solid #e0e0e0ff",
                        fontSize: "0.8rem",
                      }}
                    >
                      <option>All Types</option>
                      <option>Billing</option>
                      <option>Adjustment</option>
                      <option>Payment</option>
                    </select>
                    <ContainedButton
                      label="Go"
                      sx={{
                        minWidth: "40px",
                        height: "30px",
                        fontSize: "0.75rem",
                        p: "4px 8px",
                      }}
                      onClick={() =>
                        handleFetchWalletHistory(0, transactionRowsPerPage)
                      }
                    />
                  </Stack>
                </Stack>
              }
            />
            <Divider />
            <CardContent
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                overflow: "auto",
                height: "434px",
                p: 0,
              }}
            >
              <TableContainer sx={{ maxHeight: 434 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        #
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        TRANSACTION #
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        TYPE
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        CONTRACT
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        AMOUNT
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        BEFORE
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        AFTER
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        DESCRIPTION
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: "bold", fontSize: "0.75rem" }}
                      >
                        DATE
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactionsHistory?.map(
                      (item: TransactionItem, index: number) => (
                        <TableRow key={item.id} hover>
                          <TableCell sx={{ fontSize: "0.7rem" }}>
                            {transactionPage * transactionRowsPerPage +
                              index +
                              1}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.7rem" }}>
                            {item.transactionNumber}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.7rem" }}>
                            <Chip
                              label={item.transactionType?.toUpperCase()}
                              size="small"
                              sx={{
                                backgroundColor: getTransactionTypeColor(
                                  item.transactionType,
                                ),
                                color: "#fff",
                                fontWeight: "bold",
                                fontSize: "0.6rem",
                                height: "20px",
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.7rem" }}>
                            {item.contractNumber}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontSize: "0.7rem",
                              fontWeight: "bold",
                              color: item.amount >= 0 ? "#f44336" : " #4caf50",
                            }}
                          >
                            {Number(item.amount) < 0
                              ? (Number(item.amount) * -1).toFixed(2)
                              : (Number(item.amount) * -1).toFixed(2)}
                            {}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.7rem" }}>
                            {(Number(item.balanceBefore || 0) * -1).toFixed(2)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: "0.7rem" }}>
                            {(Number(item.balanceAfter || 0) * -1).toFixed(2)}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.7rem" }}>
                            {item.description}
                          </TableCell>
                          <TableCell sx={{ fontSize: "0.7rem" }}>
                            {formatDate(
                              item.timestamp,
                              "DD MMM YYYY, HH:mm:ss",
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {transactionsHistory?.length === 0 && (
                <Stack
                  direction={"row"}
                  justifyContent={"center"}
                  sx={{ py: 2 }}
                >
                  <Typography variant="body2">No Data found</Typography>
                </Stack>
              )}
            </CardContent>
            <TablePagination
              component="div"
              count={totalTransactions}
              page={transactionPage}
              onPageChange={handlePageChange}
              rowsPerPage={transactionRowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[5, 10, 20]}
            />
            {/* <Box sx={{ p: 1.5, borderTop: "1px solid #e0e0e0" }}>
              <Typography variant="caption" color="textSecondary">
                {transactionsHistory?.length || 0} of {totalTransactions} transactions
              </Typography>
            </Box> */}
          </Card>

          {/* <Card
            sx={{
              marginTop: "20px",
              height: "270px",
              overflow: "auto",
              borderWidth: "1px",
              borderColor: "#E0E0E0",
            }}
          >
            <CardHeader
              title={
                <Typography variant="body1" fontWeight={"600"}>
                  Work Order History
                </Typography>
              }
            />
            <Divider />

            <CardContent
              sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            >
              {workOrderHistory?.map((item: WorkOrderItem, index: number) => {
                return WorkOrderHistoryCard(item, index);
              })}

              {workOrderHistory?.length === 0 && (
                <Stack direction={"row"} justifyContent={"center"}>
                  <Typography variant="body1">No Data found</Typography>
                </Stack>
              )}
            </CardContent>
          </Card> */}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConsumerDrawerBillAndPayment;
